import { useState, useCallback, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  Calculator,
  CheckCircle,
  PlayCircle,
  Mail,
  Download,
  ArrowLeft,
  Loader2,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Minus,
} from 'lucide-react'

import { Button } from '@payroll/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@payroll/components/ui/card'
import { Badge } from '@payroll/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@payroll/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@payroll/components/ui/dialog'
import { Select } from '@payroll/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@payroll/components/ui/tabs'

import { api } from '@payroll/utils/api'
import {
  formatCurrency,
  formatDate,
  formatPeriod,
  formatMonthEn,
} from '@payroll/utils/format'
import type { PayrollPeriod, PayrollEntry, Employee } from '@payroll/types'
import { MONTHS_EN } from '@payroll/types'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PayrollEntryWithEmployee extends PayrollEntry {
  employee?: Employee
  // Flat fields from API JOIN (may come instead of nested employee)
  employee_code?: string
  nickname?: string
  first_name_th?: string
  last_name_th?: string
  first_name_en?: string
  last_name_en?: string
  department?: string
  position?: string
  ss_exempt?: boolean | number
}

/** Normalize flat API response into entry with nested employee object */
function normalizeEntry(raw: any): PayrollEntryWithEmployee {
  if (raw.employee) return raw
  if (raw.employee_code) {
    const { employee_code, nickname, title_th, first_name_th, last_name_th,
            first_name_en, last_name_en, department, position, ss_exempt,
            ...entry } = raw
    return {
      ...entry,
      employee: {
        id: raw.employee_id,
        employee_code, nickname, title_th, first_name_th, last_name_th,
        first_name_en, last_name_en, department, position,
        ss_exempt: ss_exempt === 1 || ss_exempt === true,
      } as Employee,
    }
  }
  return raw
}

function normalizeEntries(data: any[]): PayrollEntryWithEmployee[] {
  return (data ?? []).map(normalizeEntry)
}

interface DepartmentSummary {
  department: string
  employee_count: number
  total_income: number
  total_deductions: number
  total_net_pay: number
  total_tax: number
  total_ss: number
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const currentYear = new Date().getFullYear()

const STATUS_BADGE: Record<
  PayrollPeriod['status'],
  { label: string; className: string }
> = {
  draft: {
    label: 'Draft',
    className: 'bg-blue-100 text-blue-800 border-blue-200',
  },
  finalized: {
    label: 'Finalized',
    className: 'bg-green-100 text-green-800 border-green-200',
  },
  paid: {
    label: 'Paid',
    className: 'bg-purple-100 text-purple-800 border-purple-200',
  },
  emailed: {
    label: 'Emailed',
    className: 'bg-teal-100 text-teal-800 border-teal-200',
  },
}

function StatusBadge({ status }: { status: PayrollPeriod['status'] }) {
  const cfg = STATUS_BADGE[status]
  return (
    <Badge variant="outline" className={cfg.className}>
      {cfg.label}
    </Badge>
  )
}

// Editable numeric fields on a payroll entry (the ones users can type into)
// Match Excel columns exactly:
// Income: Salary, OT, Food, Diligence, Special, Bonus, Commission, Total
// Deductions: Absent, Leave, Late, Forgot Scan, Tax, SS, Other, Total
// Net Pay
const EDITABLE_INCOME_FIELDS = [
  'overtime',
  'bonus',
  'commission',
] as const

const EDITABLE_DEDUCTION_FIELDS = [
  'absent_deduction',
  'leave_deduction',
  'late_deduction',
  'income_tax',
  'social_security',
  'other_deduction',
] as const

type EditableField =
  | (typeof EDITABLE_INCOME_FIELDS)[number]
  | (typeof EDITABLE_DEDUCTION_FIELDS)[number]

// ---------------------------------------------------------------------------
// Main Page Component
// ---------------------------------------------------------------------------

export default function PayrollPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()

  // State: which tab is active
  const tabFromUrl = searchParams.get('tab') || 'periods'
  const periodIdFromUrl = searchParams.get('periodId') || ''

  const [activeTab, setActiveTab] = useState(tabFromUrl)
  const [selectedPeriodId, setSelectedPeriodId] = useState(periodIdFromUrl)

  // Keep URL in sync
  const changeTab = useCallback(
    (tab: string, periodId?: string) => {
      setActiveTab(tab)
      if (periodId !== undefined) setSelectedPeriodId(periodId)
      const params = new URLSearchParams()
      params.set('tab', tab)
      if (periodId || selectedPeriodId) {
        params.set('periodId', periodId ?? selectedPeriodId)
      }
      setSearchParams(params, { replace: true })
    },
    [selectedPeriodId, setSearchParams]
  )

  const selectPeriod = useCallback(
    (id: string) => {
      changeTab('entries', id)
    },
    [changeTab]
  )

  const goBackToPeriods = useCallback(() => {
    changeTab('periods', '')
  }, [changeTab])

  return (
    <div className="space-y-6">
      {/* Page title */}
      <div>
        <h1 className="text-2xl font-bold text-[#1a237e]">
          Payroll Management
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          Manage payroll periods, entries, and summaries
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => changeTab(v)}>
        <TabsList>
          <TabsTrigger value="periods">Periods</TabsTrigger>
          <TabsTrigger value="entries" disabled={!selectedPeriodId}>
            Payroll Entries
          </TabsTrigger>
          <TabsTrigger value="summary" disabled={!selectedPeriodId}>
            Summary
          </TabsTrigger>
        </TabsList>

        {/* -------- Tab 1: Periods -------- */}
        <TabsContent value="periods">
          <PeriodsTab onSelectPeriod={selectPeriod} />
        </TabsContent>

        {/* -------- Tab 2: Entries -------- */}
        <TabsContent value="entries">
          {selectedPeriodId ? (
            <EntriesTab
              periodId={selectedPeriodId}
              onBack={goBackToPeriods}
              onGoToSummary={() => changeTab('summary')}
            />
          ) : (
            <EmptyState message="Select a period first." />
          )}
        </TabsContent>

        {/* -------- Tab 3: Summary -------- */}
        <TabsContent value="summary">
          {selectedPeriodId ? (
            <SummaryTab periodId={selectedPeriodId} onBack={goBackToPeriods} />
          ) : (
            <EmptyState message="Select a period first." />
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}

// ---------------------------------------------------------------------------
// EmptyState
// ---------------------------------------------------------------------------

function EmptyState({ message }: { message: string }) {
  return (
    <Card>
      <CardContent className="py-12 text-center text-gray-500">
        <AlertCircle className="mx-auto h-8 w-8 mb-2 opacity-40" />
        {message}
      </CardContent>
    </Card>
  )
}

// ===========================================================================
// TAB 1 — Periods List
// ===========================================================================

function PeriodsTab({
  onSelectPeriod,
}: {
  onSelectPeriod: (id: string) => void
}) {
  const queryClient = useQueryClient()
  const [year, setYear] = useState(currentYear)
  const [showNewDialog, setShowNewDialog] = useState(false)
  const [newMonth, setNewMonth] = useState(new Date().getMonth() + 1)
  const [newYear, setNewYear] = useState(currentYear)

  // Fetch periods for selected year
  const { data: periodsRes, isLoading } = useQuery({
    queryKey: ['payroll-periods', year],
    queryFn: () =>
      api.get<PayrollPeriod[]>(`/payroll/periods?year=${year}`),
  })
  const periods = periodsRes?.data ?? []

  // Create period mutation
  const createMutation = useMutation({
    mutationFn: (body: { year: number; month: number }) =>
      api.post<PayrollPeriod>('/payroll/periods', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payroll-periods'] })
      setShowNewDialog(false)
    },
  })

  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i)

  return (
    <div className="space-y-4">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Year:</label>
          <Select
            value={String(year)}
            onChange={(e) => setYear(Number(e.target.value))}
            className="w-28"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </Select>
        </div>
        <Button onClick={() => setShowNewDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Period
        </Button>
      </div>

      {/* Periods table */}
      <Card>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#1a237e] hover:bg-[#1a237e]">
                <TableHead className="text-white font-semibold">
                  Month
                </TableHead>
                <TableHead className="text-white font-semibold">
                  Period
                </TableHead>
                <TableHead className="text-white font-semibold">
                  Pay Date
                </TableHead>
                <TableHead className="text-white font-semibold">
                  Status
                </TableHead>
                <TableHead className="text-white font-semibold text-right">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : periods.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center py-8 text-gray-500"
                  >
                    No payroll periods for {year}.
                  </TableCell>
                </TableRow>
              ) : (
                periods.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">
                      {formatMonthEn(p.month)}
                    </TableCell>
                    <TableCell>
                      {formatDate(p.period_start)} &ndash;{' '}
                      {formatDate(p.period_end)}
                    </TableCell>
                    <TableCell>{formatDate(p.pay_date)}</TableCell>
                    <TableCell>
                      <StatusBadge status={p.status} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onSelectPeriod(p.id)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* New Period dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Payroll Period</DialogTitle>
            <DialogDescription>
              Select year and month for the new payroll period.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Year</label>
              <Select
                value={String(newYear)}
                onChange={(e) => setNewYear(Number(e.target.value))}
                className="mt-1"
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">
                Month
              </label>
              <Select
                value={String(newMonth)}
                onChange={(e) => setNewMonth(Number(e.target.value))}
                className="mt-1"
              >
                {MONTHS_EN.map((name, idx) => (
                  <option key={idx} value={idx + 1}>
                    {name}
                  </option>
                ))}
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowNewDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() =>
                createMutation.mutate({ year: newYear, month: newMonth })
              }
              disabled={createMutation.isPending}
            >
              {createMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Create
            </Button>
          </DialogFooter>
          {createMutation.isError && (
            <p className="text-sm text-red-600 mt-2">
              {(createMutation.error as Error).message}
            </p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ===========================================================================
// TAB 2 — Payroll Entries
// ===========================================================================

function EntriesTab({
  periodId,
  onBack,
  onGoToSummary,
}: {
  periodId: string
  onBack: () => void
  onGoToSummary: () => void
}) {
  const queryClient = useQueryClient()

  // ---- Fetch period ----
  const { data: periodRes, isLoading: periodLoading } = useQuery({
    queryKey: ['payroll-period', periodId],
    queryFn: () => api.get<PayrollPeriod>(`/payroll/periods/${periodId}`),
  })
  const period = periodRes?.data

  // ---- Fetch entries ----
  const {
    data: entriesRes,
    isLoading: entriesLoading,
    refetch: refetchEntries,
  } = useQuery({
    queryKey: ['payroll-entries', periodId],
    queryFn: () =>
      api.get<PayrollEntryWithEmployee[]>(
        `/payroll/periods/${periodId}/entries`
      ),
  })
  const entries = normalizeEntries(entriesRes?.data as any ?? [])

  // ---- Mutations ----
  const generateMutation = useMutation({
    mutationFn: () =>
      api.post<PayrollEntryWithEmployee[]>(
        `/payroll/periods/${periodId}/entries`
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['payroll-entries', periodId],
      })
    },
  })

  const calculateAllMutation = useMutation({
    mutationFn: () =>
      api.post(`/payroll/periods/${periodId}/calculate-all`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['payroll-entries', periodId],
      })
    },
  })

  const finalizeMutation = useMutation({
    mutationFn: () =>
      api.put(`/payroll/periods/${periodId}/finalize`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['payroll-period', periodId],
      })
      queryClient.invalidateQueries({
        queryKey: ['payroll-entries', periodId],
      })
    },
  })

  const updateEntryMutation = useMutation({
    mutationFn: ({
      entryId,
      data,
    }: {
      entryId: string
      data: Partial<PayrollEntry>
    }) => api.put<PayrollEntry>(`/payroll/entries/${entryId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['payroll-entries', periodId],
      })
    },
  })

  const isFinalized = period?.status !== 'draft'
  const isLoading = periodLoading || entriesLoading

  // ---- Totals row ----
  const totals = useMemo(() => {
    const sum = (fn: (e: PayrollEntryWithEmployee) => number) =>
      entries.reduce((acc, e) => acc + fn(e), 0)
    return {
      base_salary: sum((e) => e.base_salary),
      overtime: sum((e) => e.overtime),
      bonus: sum((e) => e.bonus),
      commission: sum((e) => e.commission),
      total_income: sum((e) => e.total_income),
      absent_deduction: sum((e) => e.absent_deduction),
      leave_deduction: sum((e) => e.leave_deduction),
      late_deduction: sum((e) => e.late_deduction),
      income_tax: sum((e) => e.income_tax),
      social_security: sum((e) => e.social_security),
      other_deduction: sum((e) => e.other_deduction),
      total_deduction: sum((e) => e.total_deduction),
      net_pay: sum((e) => e.net_pay),
    }
  }, [entries])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-[#1a237e]" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h2 className="text-lg font-semibold text-[#1a237e]">
              {period
                ? formatPeriod(period.year, period.month)
                : 'Payroll Entries'}
            </h2>
            {period && (
              <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                <StatusBadge status={period.status} />
                <span>Pay date: {formatDate(period.pay_date)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-wrap">
          {entries.length === 0 && !isFinalized && (
            <Button
              onClick={() => generateMutation.mutate()}
              disabled={generateMutation.isPending}
            >
              {generateMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <PlayCircle className="h-4 w-4 mr-2" />
              )}
              Generate Entries
            </Button>
          )}
          {entries.length > 0 && !isFinalized && (
            <>
              <Button
                variant="outline"
                onClick={() => calculateAllMutation.mutate()}
                disabled={calculateAllMutation.isPending}
              >
                {calculateAllMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Calculator className="h-4 w-4 mr-2" />
                )}
                Calculate All
              </Button>
              <Button
                onClick={() => finalizeMutation.mutate()}
                disabled={finalizeMutation.isPending}
                className="bg-green-600 hover:bg-green-700"
              >
                {finalizeMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-2" />
                )}
                Finalize
              </Button>
            </>
          )}
          {entries.length > 0 && (
            <Button variant="secondary" onClick={onGoToSummary}>
              View Summary
            </Button>
          )}
        </div>
      </div>

      {/* Error alerts */}
      {generateMutation.isError && (
        <ErrorAlert message={(generateMutation.error as Error).message} />
      )}
      {calculateAllMutation.isError && (
        <ErrorAlert message={(calculateAllMutation.error as Error).message} />
      )}
      {finalizeMutation.isError && (
        <ErrorAlert message={(finalizeMutation.error as Error).message} />
      )}

      {/* Entries table */}
      {entries.length === 0 ? (
        <EmptyState message="No entries yet. Click 'Generate Entries' to create payroll entries for all active employees." />
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full caption-bottom text-xs min-w-[1400px]">
              {/* Header — matches Excel layout */}
              <thead>
                <tr className="bg-[#1a237e]">
                  <th className="sticky left-0 z-10 bg-[#1a237e] text-white font-semibold px-2 py-2 text-left whitespace-nowrap text-xs">
                    Employee
                  </th>
                  {/* Income */}
                  <th className="text-white font-semibold px-1 py-2 text-right whitespace-nowrap text-xs">Salary</th>
                  <th className="text-white font-semibold px-1 py-2 text-right whitespace-nowrap text-xs">OT</th>
                  <th className="text-white font-semibold px-1 py-2 text-right whitespace-nowrap text-xs">Bonus</th>
                  <th className="text-white font-semibold px-1 py-2 text-right whitespace-nowrap text-xs">Comm.</th>
                  <th className="text-white font-semibold px-1 py-2 text-right whitespace-nowrap text-xs bg-[#283593]">Income</th>
                  {/* Deductions */}
                  <th className="text-white font-semibold px-1 py-2 text-right whitespace-nowrap text-xs">Absent</th>
                  <th className="text-white font-semibold px-1 py-2 text-right whitespace-nowrap text-xs">Leave</th>
                  <th className="text-white font-semibold px-1 py-2 text-right whitespace-nowrap text-xs">Late</th>
                  <th className="text-white font-semibold px-1 py-2 text-right whitespace-nowrap text-xs">Tax</th>
                  <th className="text-white font-semibold px-1 py-2 text-right whitespace-nowrap text-xs">SS</th>
                  <th className="text-white font-semibold px-1 py-2 text-right whitespace-nowrap text-xs">Other</th>
                  <th className="text-white font-semibold px-1 py-2 text-right whitespace-nowrap text-xs bg-[#283593]">Deduct.</th>
                  <th className="text-white font-semibold px-1 py-2 text-right whitespace-nowrap text-xs bg-[#0d47a1]">Net Pay</th>
                </tr>
              </thead>

              <tbody>
                {entries.map((entry) => (
                  <PayrollEntryRow
                    key={entry.id}
                    entry={entry}
                    disabled={isFinalized}
                    onUpdate={(data) =>
                      updateEntryMutation.mutate({
                        entryId: entry.id,
                        data,
                      })
                    }
                  />
                ))}

                {/* Totals row */}
                <tr className="bg-gray-100 border-t-2 border-[#1a237e] font-bold text-xs">
                  <td className="sticky left-0 z-10 bg-gray-100 px-2 py-2 text-left whitespace-nowrap text-[#1a237e]">
                    TOTAL ({entries.length})
                  </td>
                  <td className="px-1 py-2 text-right whitespace-nowrap">
                    {formatCurrency(totals.base_salary)}
                  </td>
                  <td className="px-1 py-2 text-right whitespace-nowrap">
                    {formatCurrency(totals.overtime)}
                  </td>
                  <td className="px-1 py-2 text-right whitespace-nowrap">
                    {formatCurrency(totals.bonus)}
                  </td>
                  <td className="px-1 py-2 text-right whitespace-nowrap">
                    {formatCurrency(totals.commission)}
                  </td>
                  <td className="px-1 py-2 text-right whitespace-nowrap bg-blue-50 text-[#1a237e]">
                    {formatCurrency(totals.total_income)}
                  </td>
                  <td className="px-1 py-2 text-right whitespace-nowrap">
                    {formatCurrency(totals.absent_deduction)}
                  </td>
                  <td className="px-1 py-2 text-right whitespace-nowrap">
                    {formatCurrency(totals.leave_deduction)}
                  </td>
                  <td className="px-1 py-2 text-right whitespace-nowrap">
                    {formatCurrency(totals.late_deduction)}
                  </td>
                  <td className="px-1 py-2 text-right whitespace-nowrap">
                    {formatCurrency(totals.income_tax)}
                  </td>
                  <td className="px-1 py-2 text-right whitespace-nowrap">
                    {formatCurrency(totals.social_security)}
                  </td>
                  <td className="px-1 py-2 text-right whitespace-nowrap">
                    {formatCurrency(totals.other_deduction)}
                  </td>
                  <td className="px-1 py-2 text-right whitespace-nowrap bg-red-50 text-red-700">
                    {formatCurrency(totals.total_deduction)}
                  </td>
                  <td className="px-1 py-2 text-right whitespace-nowrap bg-green-50 text-green-800">
                    {formatCurrency(totals.net_pay)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Single Payroll Entry Row (with inline editing)
// ---------------------------------------------------------------------------

function PayrollEntryRow({
  entry,
  disabled,
  onUpdate,
}: {
  entry: PayrollEntryWithEmployee
  disabled: boolean
  onUpdate: (data: Partial<PayrollEntry>) => void
}) {
  // Local state for editing — we store the current values in a ref-backed
  // state so that blur sends the latest value.
  const [localValues, setLocalValues] = useState<
    Record<EditableField, string>
  >(() => {
    const init: Record<string, string> = {}
    for (const f of [...EDITABLE_INCOME_FIELDS, ...EDITABLE_DEDUCTION_FIELDS]) {
      init[f] = String(entry[f] ?? 0)
    }
    return init as Record<EditableField, string>
  })

  // Sync when entry updates from server
  useEffect(() => {
    const next: Record<string, string> = {}
    for (const f of [...EDITABLE_INCOME_FIELDS, ...EDITABLE_DEDUCTION_FIELDS]) {
      next[f] = String(entry[f] ?? 0)
    }
    setLocalValues(next as Record<EditableField, string>)
  }, [entry])

  const handleChange = (field: EditableField, value: string) => {
    setLocalValues((prev) => ({ ...prev, [field]: value }))
  }

  const handleBlur = (field: EditableField) => {
    const newVal = parseFloat(localValues[field]) || 0
    const oldVal = (entry[field] as number) ?? 0
    if (newVal !== oldVal) {
      onUpdate({ [field]: newVal })
    }
  }

  const emp = entry.employee
  const empNickname = (emp as any)?.nickname
  const empName = emp
    ? (empNickname ? `"${empNickname}" ` : '') + (emp.first_name_en && emp.last_name_en ? `${emp.first_name_en} ${emp.last_name_en}` : `${emp.first_name_th} ${emp.last_name_th}`)
    : entry.employee_id
  const empDisplay = emp ? `${emp.employee_code} ${empName}` : entry.employee_id

  const isSsExempt = emp?.ss_exempt && entry.social_security === 0

  const inputClass =
    'w-20 h-7 text-right text-xs px-1 py-0 border border-gray-300 rounded focus:ring-1 focus:ring-[#1a237e] focus:border-[#1a237e]'

  return (
    <tr className="border-b border-gray-200 hover:bg-gray-50">
      {/* Employee name — sticky */}
      <td className="sticky left-0 z-10 bg-white px-3 py-2 whitespace-nowrap text-sm font-medium group-hover:bg-gray-50">
        <div title={empDisplay}>
          {emp && (
            <span className="text-gray-400 text-xs mr-1">
              {emp.employee_code}
            </span>
          )}
          {empName}
        </div>
      </td>

      {/* Base Salary (read-only) */}
      <td className="px-2 py-2 text-right whitespace-nowrap text-sm">
        {formatCurrency(entry.base_salary)}
      </td>

      {/* Editable income fields */}
      {EDITABLE_INCOME_FIELDS.map((field) => (
        <td key={field} className="px-1 py-1 text-right">
          {disabled ? (
            <span className="text-sm">
              {formatCurrency(entry[field] as number)}
            </span>
          ) : (
            <input
              type="number"
              className={inputClass}
              value={localValues[field]}
              onChange={(e) => handleChange(field, e.target.value)}
              onBlur={() => handleBlur(field)}
              min={0}
              step={0.01}
            />
          )}
        </td>
      ))}

      {/* Total Income */}
      <td className="px-2 py-2 text-right whitespace-nowrap text-sm font-bold text-[#1a237e] bg-blue-50/50">
        {formatCurrency(entry.total_income)}
      </td>

      {/* Editable deduction fields */}
      {EDITABLE_DEDUCTION_FIELDS.map((field) => (
        <td key={field} className="px-1 py-1 text-right">
          {disabled ? (
            <span className="text-sm">
              {formatCurrency(entry[field] as number)}
            </span>
          ) : (
            <input
              type="number"
              className={inputClass}
              value={localValues[field]}
              onChange={(e) => handleChange(field, e.target.value)}
              onBlur={() => handleBlur(field)}
              min={0}
              step={0.01}
            />
          )}
        </td>
      ))}

      {/* Total Deduction */}
      <td className="px-2 py-2 text-right whitespace-nowrap text-sm font-bold text-red-700 bg-red-50/50">
        {formatCurrency(entry.total_deduction)}
      </td>

      {/* Net Pay */}
      <td className="px-2 py-2 text-right whitespace-nowrap text-sm font-bold text-green-800 bg-green-50/50">
        {formatCurrency(entry.net_pay)}
      </td>
    </tr>
  )
}

// ===========================================================================
// TAB 3 — Summary
// ===========================================================================

function SummaryTab({
  periodId,
  onBack,
}: {
  periodId: string
  onBack: () => void
}) {
  // ---- Fetch period ----
  const { data: periodRes } = useQuery({
    queryKey: ['payroll-period', periodId],
    queryFn: () => api.get<PayrollPeriod>(`/payroll/periods/${periodId}`),
  })
  const period = periodRes?.data

  // ---- Fetch entries ----
  const { data: entriesRes, isLoading } = useQuery({
    queryKey: ['payroll-entries', periodId],
    queryFn: () =>
      api.get<PayrollEntryWithEmployee[]>(
        `/payroll/periods/${periodId}/entries`
      ),
  })
  const entries = normalizeEntries(entriesRes?.data as any ?? [])

  // ---- Fetch previous period entries for comparison ----
  const previousMonth = period
    ? period.month === 1
      ? 12
      : period.month - 1
    : null
  const previousYear = period
    ? period.month === 1
      ? period.year - 1
      : period.year
    : null

  const { data: prevPeriodsRes } = useQuery({
    queryKey: ['payroll-periods', previousYear],
    queryFn: () =>
      api.get<PayrollPeriod[]>(
        `/payroll/periods?year=${previousYear}`
      ),
    enabled: !!previousYear,
  })

  const prevPeriod = (prevPeriodsRes?.data ?? []).find(
    (p) => p.month === previousMonth && p.year === previousYear
  )

  const { data: prevEntriesRes } = useQuery({
    queryKey: ['payroll-entries', prevPeriod?.id],
    queryFn: () =>
      api.get<PayrollEntryWithEmployee[]>(
        `/payroll/periods/${prevPeriod!.id}/entries`
      ),
    enabled: !!prevPeriod?.id,
  })
  const prevEntries = normalizeEntries(prevEntriesRes?.data as any ?? [])

  // ---- Compute summaries ----
  const summary = useMemo(() => {
    const sum = (fn: (e: PayrollEntryWithEmployee) => number) =>
      entries.reduce((acc, e) => acc + fn(e), 0)
    return {
      totalIncome: sum((e) => e.total_income),
      totalDeductions: sum((e) => e.total_deduction),
      totalNetPay: sum((e) => e.net_pay),
      totalTax: sum((e) => e.income_tax),
      totalSS: sum((e) => e.social_security),
    }
  }, [entries])

  const prevSummary = useMemo(() => {
    if (prevEntries.length === 0) return null
    const sum = (fn: (e: PayrollEntryWithEmployee) => number) =>
      prevEntries.reduce((acc, e) => acc + fn(e), 0)
    return {
      totalIncome: sum((e) => e.total_income),
      totalDeductions: sum((e) => e.total_deduction),
      totalNetPay: sum((e) => e.net_pay),
      totalTax: sum((e) => e.income_tax),
      totalSS: sum((e) => e.social_security),
    }
  }, [prevEntries])

  // ---- Department breakdown ----
  const departments = useMemo(() => {
    const map = new Map<string, DepartmentSummary>()
    for (const e of entries) {
      const dept = e.employee?.department ?? 'Unknown'
      const existing = map.get(dept) ?? {
        department: dept,
        employee_count: 0,
        total_income: 0,
        total_deductions: 0,
        total_net_pay: 0,
        total_tax: 0,
        total_ss: 0,
      }
      existing.employee_count += 1
      existing.total_income += e.total_income
      existing.total_deductions += e.total_deduction
      existing.total_net_pay += e.net_pay
      existing.total_tax += e.income_tax
      existing.total_ss += e.social_security
      map.set(dept, existing)
    }
    return Array.from(map.values()).sort((a, b) =>
      a.department.localeCompare(b.department)
    )
  }, [entries])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-[#1a237e]" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h2 className="text-lg font-semibold text-[#1a237e]">
              Summary &mdash;{' '}
              {period
                ? formatPeriod(period.year, period.month)
                : 'Payroll Summary'}
            </h2>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" disabled>
            <Mail className="h-4 w-4 mr-2" />
            Email All Slips
          </Button>
          <Button variant="outline" disabled>
            <Download className="h-4 w-4 mr-2" />
            Download All Slips
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <SummaryCard
          title="Total Income"
          value={summary.totalIncome}
          prev={prevSummary?.totalIncome}
          colorClass="border-l-4 border-l-blue-500"
        />
        <SummaryCard
          title="Total Deductions"
          value={summary.totalDeductions}
          prev={prevSummary?.totalDeductions}
          colorClass="border-l-4 border-l-red-500"
        />
        <SummaryCard
          title="Total Net Pay"
          value={summary.totalNetPay}
          prev={prevSummary?.totalNetPay}
          colorClass="border-l-4 border-l-green-500"
        />
        <SummaryCard
          title="Total Tax"
          value={summary.totalTax}
          prev={prevSummary?.totalTax}
          colorClass="border-l-4 border-l-amber-500"
        />
        <SummaryCard
          title="Total Social Security"
          value={summary.totalSS}
          prev={prevSummary?.totalSS}
          colorClass="border-l-4 border-l-purple-500"
        />
      </div>

      {/* Comparison with previous month */}
      {prevSummary && period && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              Comparison with Previous Month (
              {formatMonthEn(previousMonth!)} {previousYear})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <ComparisonItem
                label="Total Income"
                current={summary.totalIncome}
                previous={prevSummary.totalIncome}
              />
              <ComparisonItem
                label="Total Net Pay"
                current={summary.totalNetPay}
                previous={prevSummary.totalNetPay}
              />
              <ComparisonItem
                label="Total Tax"
                current={summary.totalTax}
                previous={prevSummary.totalTax}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Department breakdown */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Department Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#1a237e] hover:bg-[#1a237e]">
                  <TableHead className="text-white font-semibold">
                    Department
                  </TableHead>
                  <TableHead className="text-white font-semibold text-right">
                    Employees
                  </TableHead>
                  <TableHead className="text-white font-semibold text-right">
                    Total Income
                  </TableHead>
                  <TableHead className="text-white font-semibold text-right">
                    Total Deductions
                  </TableHead>
                  <TableHead className="text-white font-semibold text-right">
                    Total Tax
                  </TableHead>
                  <TableHead className="text-white font-semibold text-right">
                    Total SS
                  </TableHead>
                  <TableHead className="text-white font-semibold text-right">
                    Net Pay
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {departments.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center py-6 text-gray-500"
                    >
                      No data available.
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {departments.map((d) => (
                      <TableRow key={d.department}>
                        <TableCell className="font-medium">
                          {d.department}
                        </TableCell>
                        <TableCell className="text-right">
                          {d.employee_count}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(d.total_income)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(d.total_deductions)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(d.total_tax)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(d.total_ss)}
                        </TableCell>
                        <TableCell className="text-right font-bold">
                          {formatCurrency(d.total_net_pay)}
                        </TableCell>
                      </TableRow>
                    ))}

                    {/* Department totals */}
                    <TableRow className="bg-gray-100 border-t-2 border-[#1a237e] font-bold">
                      <TableCell>TOTAL</TableCell>
                      <TableCell className="text-right">
                        {entries.length}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(summary.totalIncome)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(summary.totalDeductions)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(summary.totalTax)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(summary.totalSS)}
                      </TableCell>
                      <TableCell className="text-right text-green-800">
                        {formatCurrency(summary.totalNetPay)}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Summary Card sub-component
// ---------------------------------------------------------------------------

function SummaryCard({
  title,
  value,
  prev,
  colorClass,
}: {
  title: string
  value: number
  prev?: number
  colorClass: string
}) {
  const diff = prev !== undefined ? value - prev : null
  const pct =
    prev !== undefined && prev !== 0
      ? ((value - prev) / prev) * 100
      : null

  return (
    <Card className={colorClass}>
      <CardContent className="pt-5 pb-4">
        <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">
          {title}
        </p>
        <p className="text-xl font-bold text-gray-900 mt-1">
          {formatCurrency(value)}
        </p>
        {diff !== null && (
          <div className="flex items-center gap-1 mt-1.5 text-xs">
            {diff > 0 ? (
              <TrendingUp className="h-3 w-3 text-red-500" />
            ) : diff < 0 ? (
              <TrendingDown className="h-3 w-3 text-green-500" />
            ) : (
              <Minus className="h-3 w-3 text-gray-400" />
            )}
            <span
              className={
                diff > 0
                  ? 'text-red-600'
                  : diff < 0
                    ? 'text-green-600'
                    : 'text-gray-500'
              }
            >
              {diff > 0 ? '+' : ''}
              {formatCurrency(diff)}
              {pct !== null && ` (${pct > 0 ? '+' : ''}${pct.toFixed(1)}%)`}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Comparison item
// ---------------------------------------------------------------------------

function ComparisonItem({
  label,
  current,
  previous,
}: {
  label: string
  current: number
  previous: number
}) {
  const diff = current - previous
  const pct = previous !== 0 ? ((current - previous) / previous) * 100 : 0

  return (
    <div className="rounded-lg bg-gray-50 p-4">
      <p className="text-xs text-gray-500 font-medium">{label}</p>
      <div className="flex items-baseline gap-3 mt-1">
        <span className="text-lg font-semibold">
          {formatCurrency(current)}
        </span>
        <span className="text-sm text-gray-400">
          vs {formatCurrency(previous)}
        </span>
      </div>
      <div className="flex items-center gap-1 mt-1 text-sm">
        {diff > 0 ? (
          <TrendingUp className="h-3.5 w-3.5 text-red-500" />
        ) : diff < 0 ? (
          <TrendingDown className="h-3.5 w-3.5 text-green-500" />
        ) : (
          <Minus className="h-3.5 w-3.5 text-gray-400" />
        )}
        <span
          className={
            diff > 0
              ? 'text-red-600'
              : diff < 0
                ? 'text-green-600'
                : 'text-gray-500'
          }
        >
          {diff > 0 ? '+' : ''}
          {formatCurrency(diff)} ({pct > 0 ? '+' : ''}
          {pct.toFixed(1)}%)
        </span>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Error alert
// ---------------------------------------------------------------------------

function ErrorAlert({ message }: { message: string }) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      <AlertCircle className="h-4 w-4 shrink-0" />
      {message}
    </div>
  )
}
